import {
  Alert,
  AlertIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Center,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  IconButton,
  Image,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  Textarea,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import dayjs from "dayjs";
import useAPI from "hooks/api";
import MainLayout from "layouts/main.layout";
import { useCallback, useEffect, useMemo, useState } from "react";

const STREAM_CDN_BASE = "https://stream-cdn.nightco.io";

// Resolve relative paths to full CDN URLs
function resolveCdnPath(path) {
  if (!path) return null;
  // Already a full URL (from admin API with token)
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  // Relative path - prepend CDN base
  const normalizedPath = path.startsWith("/") ? path.slice(1) : path;
  return `${STREAM_CDN_BASE}/${normalizedPath}`;
}

function getCDNPublicAvatarUrl(path, dim = "512") {
  if (!path?.includes("creators")) return "";
  let normalizedPath = path.split("?")[0];
  normalizedPath = normalizedPath.replace(/picture\.([a-zA-Z0-9]+)$/, `picture_${dim}.$1`);
  if (!normalizedPath.includes(`picture_${dim}.`)) return "";
  return `${STREAM_CDN_BASE}/${normalizedPath}`;
}

// Get preview URL for collage (image: media.clear, video: coverPhoto or thumbnail.clear)
function getMediaPreviewUrl(media) {
  if (!media) return null;
  if (media.type === "video") {
    // Video: prefer user-uploaded coverPhoto, then fallback to thumbnail.clear
    return resolveCdnPath(media.coverPhoto) || resolveCdnPath(media.thumbnail?.clear);
  }
  // Image: use media.clear (full URL with token)
  return media.clear || null;
}

// Get full quality URL for lightbox (image: media.clear, video: mp4.720p)
function getMediaFullUrl(media) {
  if (!media) return null;
  if (media.type === "video") {
    // Video: always use mp4.720p
    return media.mp4?.["720p"] || null;
  }
  // Image: use media.clear
  return media.clear || null;
}

// Get video poster (coverPhoto or thumbnail.clear)
function getVideoPosterUrl(media) {
  if (media?.type !== "video") return null;
  // Prefer user-uploaded coverPhoto, then fallback to thumbnail.clear
  return resolveCdnPath(media.coverPhoto) || resolveCdnPath(media.thumbnail?.clear);
}

const AspectBox = ({ ratio = "16:9", children }) => {
  const aspect = ratio === "4:3" ? 4 / 3 : ratio === "1:1" ? 1 : 16 / 9;
  return (
    <Box position="relative" width="100%" style={{ aspectRatio: aspect }}>
      <Box position="absolute" inset={0}>
        {children}
      </Box>
    </Box>
  );
};

const PreviewImage = ({ src }) => (
  <Image src={src} alt="" w="100%" h="100%" objectFit="cover" />
);

// Play button overlay for video thumbnails
const PlayButton = () => (
  <Flex
    position="absolute"
    top="50%"
    left="50%"
    transform="translate(-50%, -50%)"
    alignItems="center"
    justifyContent="center"
    pointerEvents="none"
    zIndex={2}
  >
    <svg width="48" height="48" viewBox="0 0 64 64" fill="none">
      <circle cx="32" cy="32" r="32" fill="rgba(0,0,0,0.5)" />
      <polygon
        points="26,20 26,44 46,32"
        fill="#fff"
        style={{ filter: "drop-shadow(0px 2px 4px rgba(0,0,0,0.3))" }}
      />
    </svg>
  </Flex>
);

// Media preview with optional play button for videos
const MediaPreview = ({ src, isVideo }) => (
  <Box position="relative" w="100%" h="100%">
    <PreviewImage src={src} />
    {isVideo && <PlayButton />}
  </Box>
);

// Lightbox component for fullscreen media view
function MediaLightbox({ media = [], isOpen, onClose, startIndex = 0 }) {
  const [currentIndex, setCurrentIndex] = useState(startIndex);

  useEffect(() => {
    if (isOpen) setCurrentIndex(startIndex);
  }, [isOpen, startIndex]);

  const currentMedia = media[currentIndex];
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < media.length - 1;

  const goPrev = useCallback(() => {
    if (hasPrev) setCurrentIndex((i) => i - 1);
  }, [hasPrev]);

  const goNext = useCallback(() => {
    if (hasNext) setCurrentIndex((i) => i + 1);
  }, [hasNext]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, goPrev, goNext, onClose]);

  if (!currentMedia) return null;

  const isVideo = currentMedia.type === "video";
  const mediaSrc = getMediaFullUrl(currentMedia);
  const posterSrc = isVideo ? getVideoPosterUrl(currentMedia) : null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full" isCentered>
      <ModalOverlay bg="blackAlpha.900" />
      <ModalContent bg="transparent" maxW="100vw" h="100vh" m={0} boxShadow="none">
        <ModalBody p={0} position="relative" display="flex" alignItems="center" justifyContent="center">
          {/* Close button */}
          <IconButton
            aria-label="Close"
            onClick={onClose}
            position="absolute"
            top={4}
            right={4}
            zIndex={10}
            rounded="full"
            bg="whiteAlpha.200"
            color="white"
            _hover={{ bg: "whiteAlpha.300" }}
            icon={<span style={{ fontWeight: 800, fontSize: 18 }}>✕</span>}
          />

          {/* Navigation arrows */}
          {media.length > 1 && (
            <>
              <IconButton
                aria-label="Previous"
                onClick={goPrev}
                isDisabled={!hasPrev}
                position="absolute"
                left={4}
                top="50%"
                transform="translateY(-50%)"
                zIndex={10}
                rounded="full"
                bg="whiteAlpha.200"
                color="white"
                _hover={{ bg: "whiteAlpha.300" }}
                _disabled={{ opacity: 0.3, cursor: "not-allowed" }}
                icon={<span style={{ fontWeight: 800, fontSize: 20 }}>←</span>}
              />
              <IconButton
                aria-label="Next"
                onClick={goNext}
                isDisabled={!hasNext}
                position="absolute"
                right={4}
                top="50%"
                transform="translateY(-50%)"
                zIndex={10}
                rounded="full"
                bg="whiteAlpha.200"
                color="white"
                _hover={{ bg: "whiteAlpha.300" }}
                _disabled={{ opacity: 0.3, cursor: "not-allowed" }}
                icon={<span style={{ fontWeight: 800, fontSize: 20 }}>→</span>}
              />
            </>
          )}

          {/* Counter */}
          {media.length > 1 && (
            <Text
              position="absolute"
              bottom={4}
              left="50%"
              transform="translateX(-50%)"
              color="white"
              fontSize="sm"
              bg="blackAlpha.600"
              px={3}
              py={1}
              rounded="full"
            >
              {currentIndex + 1} / {media.length}
            </Text>
          )}

          {/* Media content */}
          <Box maxW="90vw" maxH="90vh" display="flex" alignItems="center" justifyContent="center">
            {isVideo ? (
              <video
                key={mediaSrc}
                src={mediaSrc}
                poster={posterSrc || undefined}
                controls
                autoPlay
                style={{
                  maxWidth: "90vw",
                  maxHeight: "90vh",
                  objectFit: "contain",
                  background: "#000",
                }}
              />
            ) : (
              <img
                src={mediaSrc}
                alt=""
                style={{
                  maxWidth: "90vw",
                  maxHeight: "90vh",
                  objectFit: "contain",
                }}
              />
            )}
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

const Collage = ({ previews = [], onClickIndex }) => {
  const count = previews.length;
  if (count <= 0) return null;

  const handleClick = (idx) => (e) => {
    e.stopPropagation();
    if (onClickIndex) onClickIndex(idx);
  };

  if (count === 1) {
    const p = previews[0];
    return (
      <AspectBox ratio={p?.aspectRatio || "16:9"}>
        <Box cursor="pointer" onClick={handleClick(0)} w="100%" h="100%">
          <MediaPreview src={p?.url} isVideo={p?.type === "video"} />
        </Box>
      </AspectBox>
    );
  }
  return (
    <AspectBox ratio="16:9">
      <Grid templateColumns="repeat(2, 1fr)" templateRows="repeat(2, 1fr)" gap={1} w="100%" h="100%">
        {count === 2 && (
          <>
            <GridItem colSpan={1} rowSpan={2} overflow="hidden" cursor="pointer" onClick={handleClick(0)}>
              <MediaPreview src={previews[0]?.url} isVideo={previews[0]?.type === "video"} />
            </GridItem>
            <GridItem colSpan={1} rowSpan={2} overflow="hidden" cursor="pointer" onClick={handleClick(1)}>
              <MediaPreview src={previews[1]?.url} isVideo={previews[1]?.type === "video"} />
            </GridItem>
          </>
        )}
        {count === 3 && (
          <>
            <GridItem colSpan={1} rowSpan={2} overflow="hidden" cursor="pointer" onClick={handleClick(0)}>
              <MediaPreview src={previews[0]?.url} isVideo={previews[0]?.type === "video"} />
            </GridItem>
            <GridItem colSpan={1} rowSpan={1} overflow="hidden" cursor="pointer" onClick={handleClick(1)}>
              <MediaPreview src={previews[1]?.url} isVideo={previews[1]?.type === "video"} />
            </GridItem>
            <GridItem colSpan={1} rowSpan={1} overflow="hidden" cursor="pointer" onClick={handleClick(2)}>
              <MediaPreview src={previews[2]?.url} isVideo={previews[2]?.type === "video"} />
            </GridItem>
          </>
        )}
        {count >= 4 && (
          <>
            <GridItem overflow="hidden" cursor="pointer" onClick={handleClick(0)}>
              <MediaPreview src={previews[0]?.url} isVideo={previews[0]?.type === "video"} />
            </GridItem>
            <GridItem overflow="hidden" cursor="pointer" onClick={handleClick(1)}>
              <MediaPreview src={previews[1]?.url} isVideo={previews[1]?.type === "video"} />
            </GridItem>
            <GridItem overflow="hidden" cursor="pointer" onClick={handleClick(2)}>
              <MediaPreview src={previews[2]?.url} isVideo={previews[2]?.type === "video"} />
            </GridItem>
            <GridItem overflow="hidden" position="relative" cursor="pointer" onClick={handleClick(3)}>
              <MediaPreview src={previews[3]?.url} isVideo={previews[3]?.type === "video"} />
              {count > 4 && (
                <Flex
                  position="absolute"
                  inset={0}
                  bg="rgba(0,0,0,0.35)"
                  align="center"
                  justify="center"
                  pointerEvents="none"
                  zIndex={3}
                >
                  <Text fontWeight="bold" fontSize="xl" color="white">+{count - 4}</Text>
                </Flex>
              )}
            </GridItem>
          </>
        )}
      </Grid>
    </AspectBox>
  );
};

function PostCard({ post, onDeny }) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const openLightbox = useCallback((idx) => {
    setLightboxIndex(idx);
    setLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => setLightboxOpen(false), []);

  const publishTime = useMemo(() => {
    if (!post?.publishedAt && !post?.createdAt) return null;
    const raw =
      typeof post?.publishedAt === "number" || typeof post?.publishedAt === "string"
        ? post.publishedAt
        : post?.publishedAt?._seconds;
    const fallback =
      typeof post?.createdAt === "number" || typeof post?.createdAt === "string"
        ? post.createdAt
        : post?.createdAt?._seconds;
    const value = raw ?? fallback;
    if (!value) return null;
    if (typeof value === "string") {
      return dayjs(value).format("MMM D, HH:mm");
    }
    if (typeof value === "number") {
      const date = value > 1e12 ? dayjs(value) : dayjs.unix(value);
      return date.format("MMM D, HH:mm");
    }
    return null;
  }, [post?.publishedAt, post?.createdAt]);

  const mediaItems = useMemo(() => post?.media || [], [post?.media]);

  const previews = useMemo(() => {
    if (mediaItems.length === 0) return [];
    return mediaItems.slice(0, 4).map((item) => {
      const url = getMediaPreviewUrl(item);
      return {
        url: url || undefined,
        aspectRatio: item.aspectRatio || "16:9",
        type: item.type,
      };
    });
  }, [mediaItems]);

  const textWithMentions = useMemo(() => {
    if (!post?.text) return null;
    const text = post.text;
    const mentions = post?.mentions || [];
    if (mentions.length === 0) return null;

    const MENTION_REGEX = /@([a-zA-Z0-9._-]+)/g;
    const matches = [];
    let match;
    
    while ((match = MENTION_REGEX.exec(text)) !== null) {
      const slug = match[1].toLowerCase();
      const mention = mentions.find(
        (m) => m.slug?.toLowerCase() === slug || m.displayName?.toLowerCase() === slug
      );
      if (mention) {
        matches.push({
          startIndex: match.index,
          endIndex: match.index + match[0].length,
          mention,
        });
      }
    }

    if (matches.length === 0) return null;
    matches.sort((a, b) => a.startIndex - b.startIndex);

    const parts = [];
    let lastIndex = 0;

    matches.forEach((match, index) => {
      if (match.startIndex > lastIndex) {
        parts.push({
          type: "text",
          content: text.slice(lastIndex, match.startIndex),
          key: `text-${index}`,
        });
      }
      parts.push({
        type: "mention",
        content: text.slice(match.startIndex, match.endIndex),
        mention: match.mention,
        key: `mention-${index}`,
      });
      lastIndex = match.endIndex;
    });

    if (lastIndex < text.length) {
      parts.push({
        type: "text",
        content: text.slice(lastIndex),
        key: "text-end",
      });
    }

    return parts;
  }, [post?.text, post?.mentions]);

  const creatorPicture = useMemo(() => {
    if (!post?.creator?.picture) return undefined;
    return getCDNPublicAvatarUrl(post.creator.picture, "128");
  }, [post?.creator?.picture]);

  const status = post?.status;

  return (
    <Box
      borderWidth="1px"
      borderColor="rgba(255,255,255,0.08)"
      rounded="md"
      overflow="hidden"
      bg="#0f0f0f"
    >
      {/* Header with creator info and publish time */}
      <Box p={4} pb={3}>
        <Flex justify="space-between" align="center">
          <HStack spacing={3}>
            <Avatar size="sm" src={creatorPicture} />
            <VStack align="start" spacing={0}>
              <Text fontSize="sm" fontWeight="semibold" color="white">
                {post?.creator?.displayName || "Creator"}
              </Text>
              <Text fontSize="xs" color="gray.400">
                @{post?.creator?.slug || "unknown"}
              </Text>
            </VStack>
          </HStack>
          <HStack spacing={2}>
            {status && (
              <Badge
                colorScheme={
                  status === "published"
                    ? "green"
                    : status === "processing"
                      ? "yellow"
                      : status === "denied"
                        ? "red"
                        : "gray"
                }
                textTransform="capitalize"
              >
                {status}
              </Badge>
            )}
            {publishTime && (
              <Text fontSize="xs" color="gray.500">
                {publishTime}
              </Text>
            )}
          </HStack>
        </Flex>
      </Box>

      {/* Denied reason */}
      {status === "denied" && post?.deniedReason && (
        <Box px={4} pb={3}>
          <Box
            bg="red.900"
            border="1px solid"
            borderColor="red.700"
            rounded="md"
            px={3}
            py={2}
          >
            <Text fontSize="xs" color="red.300" fontWeight="semibold" mb={1}>
              拒絕原因
            </Text>
            <Text fontSize="sm" color="red.100">
              {post.deniedReason}
            </Text>
          </Box>
        </Box>
      )}

      {/* Post text above media */}
      {post?.text && (
        <Box px={4} pt={4} pb={2}>
          <Text noOfLines={3} color="white" fontSize="sm" wordBreak="break-word">
            {textWithMentions ? (
              textWithMentions.map((part) => {
                if (part.type === "mention") {
                  return (
                    <Text
                      key={part.key}
                      as="span"
                      color="blue.300"
                      fontWeight="bold"
                      px={0.5}
                      py={0.5}
                      title={part.mention.displayName}
                    >
                      {part.content}
                    </Text>
                  );
                }
                return <span key={part.key}>{part.content}</span>;
              })
            ) : (
              post.text
            )}
          </Text>
        </Box>
      )}

      {/* Mentions row */}
      {post?.mentions && post.mentions.length > 0 && (
        <Box px={4} py={3}>
          <Flex gap={0} wrap="wrap" align="center">
            {post.mentions.map((mention, idx) => (
              <Flex align="center" key={mention.id}>
                <Text color="blue.400" as="span">
                  {mention.displayName}
                </Text>
                <Text color="whiteAlpha.600" fontSize="2xs" as="span" px={1}>
                  @{mention.slug}
                </Text>
                {idx !== post.mentions.length - 1 && (
                  <Box
                    height="18px"
                    width="1.5px"
                    bg="gray.500"
                    mx={2}
                    borderRadius="full"
                  />
                )}
              </Flex>
            ))}
          </Flex>
        </Box>
      )}

      {/* Media Collage */}
      {previews.length > 0 && (
        <Collage previews={previews} onClickIndex={openLightbox} />
      )}

      {/* Lightbox for fullscreen view */}
      <MediaLightbox
        media={mediaItems}
        isOpen={lightboxOpen}
        onClose={closeLightbox}
        startIndex={lightboxIndex}
      />

      {/* Footer with tags, stats and actions */}
      <Box p={4}>
        <Flex gap={2} wrap="wrap">
          {(post?.tags || []).map((tag) => (
            <Badge key={tag} colorScheme="blue" variant="subtle">
              #{tag}
            </Badge>
          ))}
        </Flex>
        <Flex mt={3} align="center" justify="space-between">
          <HStack spacing={4} fontSize="sm" color="gray.400">
            <Text>{post?.media?.length ?? 0} media</Text>
            <Text>❤️ {post?.likedCount || 0}</Text>
            <Text>🔖 {post?.savedCount || 0}</Text>
          </HStack>
          {status !== "denied" && (
            <Button
              size="sm"
              colorScheme="red"
              onClick={() => onDeny(post)}
            >
              拒絕
            </Button>
          )}
        </Flex>
      </Box>

      {/* Mentioned creators cards */}
      {post?.mentions && post.mentions.length > 0 && (
        <Box
          px={4}
          py={3}
          bg="rgba(255,255,255,0.03)"
          borderTop="1px solid rgba(255,255,255,0.08)"
        >
          <VStack spacing={3} align="stretch">
            {post.mentions.map((mention) => {
              const picturePath = mention.id
                ? `creators/${mention.id}/picture.webp`
                : null;
              const coverSrc = picturePath
                ? getCDNPublicAvatarUrl(picturePath, "512")
                : undefined;
              const avatarSrc = getCDNPublicAvatarUrl(`creators/${mention.id}/picture.webp`, "128");
              const creatorSlug = mention.slug;
              return (
                <Box
                  key={mention.id || creatorSlug}
                  rounded="lg"
                  overflow="hidden"
                  bg="#101014"
                  transition="all 0.2s"
                  position="relative"
                  sx={{
                    backgroundImage: coverSrc ? `url(${coverSrc})` : undefined,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                  p={4}
                  display="flex"
                  alignItems="flex-end"
                  minH="0"
                >
                  {/* Semi-transparent dim overlay */}
                  <Box
                    position="absolute"
                    left={0}
                    right={0}
                    bottom={0}
                    height="50%"
                    bg={coverSrc ? "rgba(0,0,0,0.5)" : "none"}
                    zIndex={1}
                  />
                  {/* Avatar, display name, and handle */}
                  <Flex
                    position="relative"
                    zIndex={2}
                    align="flex-end"
                    w="100%"
                  >
                    <Avatar size="xl" src={avatarSrc} mr={3} alignSelf="flex-end" />
                    <Box pb={1} flex="1" minW={0}>
                      <Text
                        fontWeight="bold"
                        color="white"
                        fontSize="2xl"
                        mb={0}
                        lineHeight="short"
                      >
                        {mention.displayName}
                      </Text>
                      <Text fontSize="md" color="whiteAlpha.700" lineHeight="short">
                        @{creatorSlug}
                      </Text>
                    </Box>
                  </Flex>
                </Box>
              );
            })}
          </VStack>
        </Box>
      )}
    </Box>
  );
}

export default function ReviewPostsPage() {
  const toast = useToast();
  const api = useAPI();
  const [denyingPost, setDenyingPost] = useState(null);
  const {
    isOpen: isDenyOpen,
    onOpen: onDenyOpen,
    onClose: onDenyClose,
  } = useDisclosure();
  const [denyReason, setDenyReason] = useState("");
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const loadPosts = useCallback(
    async (cursor = null) => {
      try {
        if (cursor) {
          setIsLoadingMore(true);
        } else {
          setIsLoading(true);
          setError(null);
        }

        const res = await api.listPosts(cursor ? { next: cursor, limit: 20 } : { limit: 20 });
        const newPosts = res?.data || [];
        const next = res?.paginator?.next || res?.next || null;

        if (cursor) {
          setPosts((prev) => [...prev, ...newPosts]);
        } else {
          setPosts(newPosts);
        }
        setNextCursor(next);
      } catch (err) {
        console.error("Error loading posts:", err);
        setError(err.message || "載入失敗");
        toast({
          title: "載入失敗",
          description: err.message || "請稍後再試",
          status: "error",
        });
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [api, toast],
  );

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  const handleDeny = useCallback(
    (post) => {
      setDenyingPost(post);
      setDenyReason("");
      onDenyOpen();
    },
    [onDenyOpen],
  );

  const handleDenyConfirm = useCallback(async () => {
    if (!denyingPost || !denyReason.trim()) {
      toast({
        title: "請輸入拒絕原因",
        status: "warning",
      });
      return;
    }

    try {
      await api.denyPost(denyingPost.id, denyReason.trim());
      toast({
        title: "已拒絕該貼文",
        status: "success",
      });
      // Reload posts to reflect the change
      loadPosts();
      onDenyClose();
      setDenyingPost(null);
      setDenyReason("");
    } catch (error) {
      console.error("Error denying post:", error);
      toast({
        title: "拒絕失敗",
        description: error.message || "請稍後再試",
        status: "error",
      });
    }
  }, [denyingPost, denyReason, api, toast, loadPosts, onDenyClose]);

  const handleLoadMore = useCallback(() => {
    if (nextCursor && !isLoadingMore) {
      loadPosts(nextCursor);
    }
  }, [nextCursor, isLoadingMore, loadPosts]);

  return (
    <MainLayout>
      <Box px={{ base: 3, md: 6 }} py={4} maxW="860px" mx="auto">
        <Heading size="md" mb={4} color="white">
          貼文審核
        </Heading>

        {isLoading && (
          <Center py={10}>
            <Spinner size="xl" />
          </Center>
        )}

        {error && (
          <Alert status="error" mb={4}>
            <AlertIcon />
            載入失敗，請稍後再試
          </Alert>
        )}

        {!isLoading && !error && (
          <>
            {posts.length === 0 ? (
              <Alert status="info">
                <AlertIcon />
                目前沒有需要審核的貼文
              </Alert>
            ) : (
              <Grid templateColumns={{ base: "1fr", md: "repeat(1, 1fr)" }} gap={4}>
                {posts.map((post) => (
                  <GridItem key={post.id}>
                    <PostCard post={post} onDeny={handleDeny} />
                  </GridItem>
                ))}
              </Grid>
            )}

            {nextCursor && (
              <VStack mt={6}>
                <Button
                  onClick={handleLoadMore}
                  isLoading={isLoadingMore}
                  colorScheme="blue"
                  variant="outline"
                >
                  載入更多
                </Button>
              </VStack>
            )}
          </>
        )}
      </Box>

      <Modal isOpen={isDenyOpen} onClose={onDenyClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>拒絕貼文</ModalHeader>
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <Text color="gray.300">
                確定要拒絕此貼文嗎？此操作將標記貼文為已拒絕狀態。
              </Text>
              <Textarea
                placeholder="請輸入拒絕原因..."
                value={denyReason}
                onChange={(e) => setDenyReason(e.target.value)}
                minH="100px"
              />
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDenyClose}>
              取消
            </Button>
            <Button colorScheme="red" onClick={handleDenyConfirm}>
              確認拒絕
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </MainLayout>
  );
}

